import { useIsFocused } from "@react-navigation/native";
import {
  OrderStatus,
  QueryMode,
  SortDirection,
  TicketStatus,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { useTicketListQuery } from "@ticketingplatform/api/dist/ticket";
import { useEventOccurrence } from "views/EventDetails/hooks";
import { useCallback, useMemo, useState } from "react";
import { QueryHookOptions } from "@apollo/client";
import {
  TicketList,
  TicketListVariables,
} from "@ticketingplatform/api/dist/ticket/__generated__/TicketList";

import { TicketListProps } from "./partials/TicketList/types";

export const useTicketList = (query: string): TicketListProps => {
  const isFocused = useIsFocused();
  const { eventOccurrence } = useEventOccurrence();
  const [loadingMore, setLoadingMore] = useState(false);

  const options = useMemo<
    QueryHookOptions<TicketList, TicketListVariables>
  >(() => {
    if (!isFocused || !eventOccurrence) {
      return { skip: true };
    }

    return {
      variables: {
        orderInclude: true,
        take: 20,
        skip: 0,
        orderBy: [
          { order: { firstName: SortDirection.asc } },
          { order: { lastName: SortDirection.asc } },
          { ticketNumber: SortDirection.asc },
          { id: SortDirection.asc },
        ],
        filters: {
          status: {
            in: [TicketStatus.NEW, TicketStatus.SCANNED],
          },
          date: {
            equals: eventOccurrence.date,
          },
          ticketPool: {
            eventId: {
              equals: eventOccurrence.event.id,
            },
          },
          order: {
            status: {
              equals: OrderStatus.COMPLETED,
            },
          },
          ...(query
            ? {
                OR: [
                  {
                    ticketNumber: {
                      contains: query,
                      mode: QueryMode.insensitive,
                    },
                  },
                  {
                    order: {
                      firstName: {
                        contains: query,
                        mode: QueryMode.insensitive,
                      },
                    },
                  },
                  {
                    order: {
                      lastName: {
                        contains: query,
                        mode: QueryMode.insensitive,
                      },
                    },
                  },
                ],
              }
            : {}),
        },
      },
    };
  }, [eventOccurrence, isFocused, query]);

  const { data, loading, fetchMore } = useTicketListQuery(options);

  const tickets = data?.tickets?.nodes || [];
  const count = data?.tickets?.count || 0;

  const onEndReached = useCallback(async () => {
    if (tickets.length >= count || loading || loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          skip: tickets.length,
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [count, fetchMore, loading, loadingMore, tickets.length]);

  return {
    loading,
    loadingMore,
    onEndReached,
    data: tickets,
    eventOccurrenceId: eventOccurrence?.id,
  };
};
